import { useState } from 'react';
import { t as tg, useT } from '@hana/i18n';
import './i18n';
import {
  IconArrowBackUp, IconArrowDown, IconArrowUp, IconCircleOff, IconDeviceFloppy, IconFilePlus, IconLayersSubtract,
  IconRectangle, IconRotate, IconRotateClockwise, IconScissors, IconTextPlus, IconTrash,
} from '@tabler/icons-react';
import { Button, Modal } from '@hana/ui';
import { PDF_BC, PDF_MAIL, PDF_SI, type PdfPage } from './viewerData';
import s from './pdfEditor.module.css';

// Mint-FileViewer · mode=edit (PDF Editor): thanh đầu "PDF Editor · N thao tác" + Hoàn tác / Huỷ sửa / Lưu bản sửa; thanh công cụ 9 nút,
// lưới trang 4 cột, ô "Thao tác chờ lưu". Mọi thao tác chỉ ở bộ nhớ trang; lưu ra tệp báo toast "Chưa mở ghi ở bản này".

export interface EdPage { src: number; rot: number }
export interface EdState { pages: EdPage[]; texts: Record<number, string>; redacts: Record<number, true>; ops: string[] }

const LABELS: Record<string, string> = {
  'BOOKING CONFIRMATION': 'fv.pg.bc', 'EQUIPMENT RELEASE': 'fv.pg.er', 'TERMS & CONDITIONS': 'fv.pg.terms',
  'SHIPPING INSTRUCTION': 'fv.pg.si', 'CONTAINER DETAILS': 'fv.pg.cont', REMARKS: 'fv.pg.remarks', 'BOOKING CANCELLATION': 'fv.pg.cancel', TERMS: 'fv.pg.terms',
};
const MERGED: string[] = ['fv.pg.mImg', 'fv.pg.mDrop'];
const LINES = ['92%', '80%', '86%', '60%', '88%', '74%', '90%', '52%'] as const;

export const basePages = (src: readonly PdfPage[]): EdPage[] => src.map((_, i) => ({ src: i, rot: 0 }));
export const pickPages = (kind: string, code: string): readonly PdfPage[] => (kind === 'mail' ? PDF_MAIL : code === 'SI' ? PDF_SI : PDF_BC);
const labelOf = (src: readonly PdfPage[], p: EdPage) => (p.src >= 100 ? tg(MERGED[(p.src - 100) % 2]!) : LABELS[src[p.src]!.head] ? tg(LABELS[src[p.src]!.head]!) : src[p.src]!.head);
export const emptyEd = (src: readonly PdfPage[]): EdState => ({ pages: basePages(src), texts: {}, redacts: {}, ops: [] });

export function usePdfEditor(src: readonly PdfPage[]) {
  const [ed, setEd] = useState<EdState>(() => emptyEd(src));
  const [hist, setHist] = useState<EdState[]>([]);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [textOpen, setTextOpen] = useState(false);
  const [textVal, setTextVal] = useState(() => tg('fv.defText'));
  const idx = [...sel].filter((i) => i < ed.pages.length).sort((a, b) => a - b);
  const apply = (fn: (e: EdState) => void, label: string) => {
    const n: EdState = { pages: ed.pages.map((x) => ({ ...x })), texts: { ...ed.texts }, redacts: { ...ed.redacts }, ops: [...ed.ops, label] };
    fn(n);
    setHist([...hist, ed]);
    setEd(n);
  };
  const reset = (to?: EdState) => { setEd(to ?? emptyEd(src)); setHist([]); setSel(new Set()); setTextOpen(false); };
  const undo = () => { const h = hist[hist.length - 1]; if (!h) return; setEd(h); setHist(hist.slice(0, -1)); setSel(new Set()); };
  return { ed, hist, sel, setSel, idx, apply, reset, undo, textOpen, setTextOpen, textVal, setTextVal };
}
export type PdfEditor = ReturnType<typeof usePdfEditor>;

export function EditorBar({ e, onCancel, onSave }: { e: PdfEditor; onCancel: () => void; onSave: () => void }) {
  const t = useT();
  return (
    <>
      <span className={s.opsK}>{t('fv.edBar', { n: e.ed.ops.length })}</span>
      <Button variant="ghost" icon={<IconArrowBackUp size={16} />} disabled={!e.hist.length} onClick={e.undo}>{t('fv.undo')}</Button>
      <Button variant="ghost" icon={<IconCircleOff size={16} />} onClick={onCancel}>{t('fv.edCancel')}</Button>
      <Button variant="primary" icon={<IconDeviceFloppy size={16} />} disabled={!e.ed.ops.length} onClick={onSave}>{t('fv.edSave')}</Button>
    </>
  );
}

export function LeaveModal({ open, n, onStay, onDiscard }: { open: boolean; n: number; onStay: () => void; onDiscard: () => void }) {
  const t = useT();
  return (
    <Modal open={open} onOpenChange={(o) => !o && onStay()} title={t('fv.leaveT', { n })}
      footer={<><Button variant="ghost" onClick={onStay}>{t('fv.keepEd')}</Button><Button variant="primary" onClick={onDiscard}>{t('fv.discard')}</Button></>}>
      {t('fv.leaveD')}
    </Modal>
  );
}

export function PdfEditorBody({ e, src, name, onMerge, onToast }: { e: PdfEditor; src: readonly PdfPage[]; name: string; onMerge: () => void; onToast: (m: string) => void }) {
  const t = useT();
  const { ed, sel, idx } = e;
  const none = !idx.length;
  const nums = idx.map((i) => i + 1).join(', ');
  const stem = name.replace(/\.pdf$/i, '');
  const tools: { k: string; Ic: typeof IconRotate; go: () => void; dis: boolean; on?: boolean }[] = [
    { k: t('fv.tool.rl'), Ic: IconRotate, dis: none, go: () => e.apply((n) => idx.forEach((i) => { n.pages[i]!.rot = (n.pages[i]!.rot + 270) % 360; }), t('fv.op.rl', { p: nums })) },
    { k: t('fv.tool.rr'), Ic: IconRotateClockwise, dis: none, go: () => e.apply((n) => idx.forEach((i) => { n.pages[i]!.rot = (n.pages[i]!.rot + 90) % 360; }), t('fv.op.rr', { p: nums })) },
    { k: t('fv.tool.del'), Ic: IconTrash, dis: none || idx.length >= ed.pages.length, go: () => { e.apply((n) => { n.pages = n.pages.filter((_, i) => !idx.includes(i)); }, t('fv.op.del', { p: nums })); e.setSel(new Set()); } },
    { k: t('fv.tool.up'), Ic: IconArrowUp, dis: idx.length !== 1 || idx[0] === 0, go: () => { const i = idx[0]!; e.apply((n) => { const x = n.pages.splice(i, 1)[0]!; n.pages.splice(i - 1, 0, x); }, t('fv.op.up', { p: i + 1 })); e.setSel(new Set([i - 1])); } },
    { k: t('fv.tool.down'), Ic: IconArrowDown, dis: idx.length !== 1 || idx[0]! >= ed.pages.length - 1, go: () => { const i = idx[0]!; e.apply((n) => { const x = n.pages.splice(i, 1)[0]!; n.pages.splice(i + 1, 0, x); }, t('fv.op.down', { p: i + 1 })); e.setSel(new Set([i + 1])); } },
    { k: t('fv.tool.split'), Ic: IconScissors, dis: none, go: () => onToast(t('fv.op.split', { p: nums, f: `${stem}_p${idx.map((i) => i + 1).join('-')}.pdf` })) },
    { k: t('fv.tool.merge'), Ic: IconFilePlus, dis: false, go: onMerge },
    { k: t('fv.tool.text'), Ic: IconTextPlus, dis: idx.length !== 1, on: e.textOpen, go: () => e.setTextOpen(true) },
    { k: t('fv.tool.redact'), Ic: IconRectangle, dis: none, go: () => e.apply((n) => idx.forEach((i) => { n.redacts[i] = true; }), t('fv.op.redact', { p: nums })) },
  ];
  const insert = () => {
    const v = e.textVal.trim(); const i = idx[0] ?? 0;
    if (!v) return;
    e.apply((n) => { n.texts[i] = v; }, t('fv.op.text', { v, p: i + 1 }));
    e.setTextOpen(false);
  };
  return (
    <div className={s.wrap}>
      <div role="toolbar" aria-label={t('fv.pdfTools')} className={s.tools}>
        {tools.map(({ k, Ic, go, dis, on }) => (
          <button key={k} type="button" className={s.tool} disabled={dis} aria-pressed={!!on} onClick={go}><Ic size={15} aria-hidden="true" />{k}</button>
        ))}
      </div>
      <div className={s.hint}>{idx.length ? t('fv.hintSel', { p: nums }) : t('fv.hintNone')}</div>
      {e.textOpen && (
        <div className={s.textBar}>
          <b>{t('fv.textIn', { p: (idx[0] ?? 0) + 1 })}</b>
          <input aria-label={t('fv.textVal')} value={e.textVal} onChange={(ev) => e.setTextVal(ev.target.value)} />
          <Button variant="primary" icon={<IconLayersSubtract size={16} />} onClick={insert}>{t('fv.insert')}</Button>
        </div>
      )}
      <div className={s.grid}>
        {ed.pages.map((pg, i) => {
          const on = sel.has(i);
          const k = labelOf(src, pg);
          return (
            <div key={`${pg.src}-${i}`} className={s.cell}>
              <button type="button" className={s.page} aria-pressed={on} aria-label={t('fv.pageOf', { n: i + 1, k })} style={{ transform: `rotate(${pg.rot}deg)` }}
                onClick={() => { const n = new Set(sel); if (on) n.delete(i); else n.add(i); e.setSel(n); }}>
                <span className={s.pT} />
                {LINES.map((w, j) => <span key={j} className={s.pL} style={{ width: w }} />)}
                {ed.redacts[i] && <span className={s.pRed} />}
                {ed.texts[i] && <span className={s.pTxt}>{ed.texts[i]}</span>}
                {pg.src >= 100 && <span className={s.pMerged}>{t('fv.merged')}</span>}
              </button>
              <div className={s.lbl} data-on={on}>{i + 1} · {k}</div>
            </div>
          );
        })}
      </div>
      <section className={s.pending}>
        <h3>{t('fv.pending')}</h3>
        {!ed.ops.length && <div className={s.none}>{t('fv.noChange')}</div>}
        <ol>{ed.ops.map((o, i) => <li key={i}>{o}</li>)}</ol>
        <div className={s.note}>{t('fv.saveOut')}<b>{stem}_edited.pdf</b>{t('fv.saveBeside')}{t('fv.saveKeep')}</div>
      </section>
    </div>
  );
}
